'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { cn } from '../../../utils/cn';
import { Button } from '../Button';
type CategoryOptionLike = {
  value: string;
  category?: string | null;
};
interface CategoryControlsProps<TOption extends CategoryOptionLike> {
  options: TOption[];
  values: string[];
  formatCategoryLabel?: (categoryId: string) => string;
  onSelect: (values: string[]) => void;
  onSortingCategoryChange: (category: string | null) => void;
}
const activeCategoryClassName = 'border-[0.5px] border-border bg-secondary shadow-sm hover:bg-accent active:bg-accent dark:border-transparent dark:bg-secondary dark:shadow-none dark:hover:bg-secondary dark:active:bg-secondary/80';
export function CategoryControls(t0) {
  const $ = _c(35);
  const {
    options,
    values,
    formatCategoryLabel,
    onSelect,
    onSortingCategoryChange
  } = t0;
  let map;
  if ($[0] !== options) {
    map = new Map();
    for (const option of options) {
      const category = option.category;
      if (category == null) {
        continue;
      }
      const set = map.get(category) ?? new Set();
      set.add(option.value);
      map.set(category, set);
    }
    $[0] = options;
    $[1] = map;
  } else {
    map = $[1];
  }
  const categoryMap = map;
  let t1;
  if ($[2] !== onSelect || $[3] !== onSortingCategoryChange) {
    t1 = () => {
      onSelect([]);
      onSortingCategoryChange(null);
    };
    $[2] = onSelect;
    $[3] = onSortingCategoryChange;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const resetSelection = t1;
  let t2;
  if ($[5] !== categoryMap || $[6] !== values) {
    bb0: {
      if (values.length === 0) {
        t2 = "all";
        break bb0;
      }
      const selectedSet = new Set(values);
      for (const [category_0, categorySet] of categoryMap) {
        if (categorySet.size !== selectedSet.size) {
          continue;
        }
        let matches = true;
        for (const val of selectedSet) {
          if (!categorySet.has(val)) {
            matches = false;
            break;
          }
        }
        if (matches) {
          t2 = category_0;
          break bb0;
        }
      }
      t2 = null;
    }
    $[5] = categoryMap;
    $[6] = values;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const activeCategory = t2;
  let t3;
  if ($[8] !== activeCategory || $[9] !== categoryMap || $[10] !== onSelect || $[11] !== onSortingCategoryChange || $[12] !== resetSelection) {
    t3 = category_1 => {
      if (category_1 === "all") {
        return resetSelection();
      }
      const categorySet_0 = categoryMap.get(category_1);
      if (!categorySet_0) {
        return;
      }
      const isCurrentlyActive = activeCategory === category_1;
      if (isCurrentlyActive) {
        return resetSelection();
      }
      onSelect([...categorySet_0]);
      onSortingCategoryChange(category_1);
    };
    $[8] = activeCategory;
    $[9] = categoryMap;
    $[10] = onSelect;
    $[11] = onSortingCategoryChange;
    $[12] = resetSelection;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleCategoryChange = t3;
  if (categoryMap.size === 0) {
    return null;
  }
  let t4;
  if ($[14] !== handleCategoryChange) {
    t4 = () => handleCategoryChange("all");
    $[14] = handleCategoryChange;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = activeCategory === "all" && activeCategoryClassName;
  let t6;
  if ($[16] !== t5) {
    t6 = cn("text-foreground text-3xs border-border shrink-0 cursor-pointer rounded-lg border px-2 py-1", "hover:bg-muted/50 active:bg-muted/70", t5);
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t6) {
    t7 = <button type="button" data-testid="category-chip-all" onClick={t4} className={t6}>All</button>;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== activeCategory || $[22] !== categoryMap || $[23] !== formatCategoryLabel || $[24] !== handleCategoryChange) {
    t8 = Array.from(categoryMap.keys()).map(category_2 => <button key={category_2} type="button" data-testid={`category-chip-${category_2}`} onClick={() => handleCategoryChange(category_2)} className={cn("text-foreground text-3xs border-border shrink-0 cursor-pointer rounded-lg border px-2 py-1 capitalize", "hover:bg-muted/50 active:bg-muted/70", activeCategory === category_2 && activeCategoryClassName)}>{formatCategoryLabel?.(category_2) ?? category_2}</button>);
    $[21] = activeCategory;
    $[22] = categoryMap;
    $[23] = formatCategoryLabel;
    $[24] = handleCategoryChange;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t7 || $[27] !== t8) {
    t9 = <div className="gap-2xs flex min-w-0 flex-1 [scrollbar-width:thin] items-center overflow-x-auto overflow-y-hidden">{t7}{t8}</div>;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = values.length === 0;
  let t11;
  if ($[29] !== resetSelection || $[30] !== t10) {
    t11 = <Button disabled={t10} onClick={resetSelection} variant="ghost" size="xs" className="shrink-0">Clear</Button>;
    $[29] = resetSelection;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t9) {
    t12 = <div className="gap-2xs flex min-w-0 flex-1 items-center">{t9}{t11}</div>;
    $[32] = t11;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}